import React from 'react';
import { computed, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { deserializeNodeValue, EventType, PER_PAGE, serializeNodeValue, EventTreeType, TreeType } from './utils';
import {
    apiGetEventsRequest,
    ApiGetEventsResponse200Type,
    ApiGetEventsResponseType,
} from 'src/api_openapi/v2/trading-search/apiGetEvents';
import { MarketStoreState } from './MarketStore.state';
import { DateTime } from 'src_common/utils/time/time';

export class EventStoreState {
    private readonly allEventsResource: AutoMap<number, Resource<ApiGetEventsResponseType | null>>;
    private readonly marketStore: AutoMap<string, MarketStoreState>;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string,
        private readonly competitionId: string,
        private readonly loadedNodes: () => Set<string>,
        private readonly readOnlyMode: boolean
    ) {
        this.allEventsResource = new AutoMap((page) => {
            return new Resource(() => this.fetchEvents(page));
        });

        this.marketStore = new AutoMap((eventId) => {
            return new MarketStoreState(common, this.sportId, this.competitionId, eventId, this.readOnlyMode);
        });

        makeAutoObservable(this);
    }

    private async fetchEvents(page: number): Promise<ApiGetEventsResponseType> {
        const getEventsReponse = await apiGetEventsRequest(
            `${this.common.appEnvParams.gatewayUrl}`,
            {
                sportId: this.sportId,
                competitionId: this.competitionId,
                page,
                pageSize: PER_PAGE,
                active: true,
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );
        return getEventsReponse;
    }

    private getEventsPage = (page: number): ApiGetEventsResponse200Type | 'loading' | 'error' => {
        const res = this.allEventsResource.get(page).get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error' || res.value === null || res.value.status !== 200) {
            return 'error';
        }

        return res.value.body;
    };

    @computed public get getEvents(): EventTreeType[] | 'loading' | 'error' {
        const fetchedItems: Array<EventType> = [];
        const resFirstPage = this.getEventsPage(1);

        if (resFirstPage === 'loading') {
            return 'loading';
        }

        if (resFirstPage === 'error') {
            return 'error';
        }

        resFirstPage.items.map((item) => {
            fetchedItems.push(item);
        });

        // load other pages
        const pageCount = resFirstPage.pagination.pageCount;
        if (pageCount > 1) {
            for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
                const resAnotherPage = this.getEventsPage(currentPage);

                if (resAnotherPage === 'loading') {
                    return 'loading';
                }

                if (resAnotherPage === 'error') {
                    return 'error';
                }

                resAnotherPage.items.map((item) => {
                    fetchedItems.push(item);
                });
            }
        }

        return fetchedItems.map((event) => {
            let markets: Array<TreeType> = [];

            if (this.getLoadedEventIds.includes(event.eventId)) {
                const data = this.marketStore.get(event.eventId).getMarkets;

                if (data === 'error') {
                    // handle error
                    // Toast ?
                }

                if (data !== 'loading' && data !== 'error') {
                    markets = data;
                }
            }
            const eventId = event.eventId;
            const eventName = event.name;
            const eventStartTime = DateTime.from(event.timeSettings.startTime);
            const serializedValue = serializeNodeValue({
                type: 'event',
                sportId: this.sportId,
                competitionId: this.competitionId,
                eventId,
            });
            return {
                id: serializedValue,
                value: serializedValue,
                name: eventName,
                label: (
                    <label className='flex flex-col'>
                        {eventName}
                        <span className='text-muted-foreground text-xs'>
                            Start date: {eventStartTime?.format('YYYY-MM-DD')} | ID: {eventId}
                        </span>
                    </label>
                ),
                showCheckbox: !this.readOnlyMode,
                children: markets,
            };
        });
    }

    @computed public get getLoadedEventIds(): string[] {
        const loadedEventIds: Array<string> = [];
        for (const nodeValue of this.loadedNodes()) {
            const loadedDeserialized = deserializeNodeValue(nodeValue);
            if (loadedDeserialized !== null && loadedDeserialized.type === 'event') {
                // only current context
                if (
                    loadedDeserialized.sportId === this.sportId &&
                    loadedDeserialized.competitionId === this.competitionId
                ) {
                    loadedEventIds.push(loadedDeserialized.eventId);
                }
            }
        }
        return loadedEventIds;
    }

    @computed public get isLoading(): boolean {
        if (this.getEvents === 'loading') {
            return true;
        }

        let isLoading = false;
        for (const eventId of this.getLoadedEventIds) {
            if (this.marketStore.get(eventId).isLoading === true) {
                isLoading = true;
            }
        }
        return isLoading;
    }
}
